import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import {
  BookOpen, Users, Filter, X, ChevronDown, ChevronUp, Briefcase, Scale, Tag, Zap,
  Plus, Pencil, Trash2, ChevronLeft, ChevronRight, LogIn,
} from "lucide-react";
import type { NegotiationCase, RolePair } from "@shared/schema";

const ITEMS_PER_PAGE = 6;

// simple browser-safe uuid
function uid() {
  return "rp-" + Math.random().toString(36).slice(2, 10);
}

interface Filters {
  scenarioTypes: string[];
  domainTypes: string[];
  authorCollections: string[];
  negotiationTypes: string[];
}

const ALL_VALUE = "__all__";

const DIFFICULTY_OPTIONS = [
  { value: "easy", label: "Простой" },
  { value: "medium", label: "Средний" },
  { value: "hard", label: "Сложный" },
];

function emptyRolePair(): RolePair {
  return { id: uid(), roleA: "", roleB: "", goalA: "", goalB: "" };
}

interface CaseFormData {
  title: string;
  description: string;
  context: string;
  author: string;
  scenarioType: string;
  domain: string;
  authorCollection: string;
  negotiationType: string;
  maxRounds: number;
  difficulty: string;
  rolePairs: RolePair[];
}

function emptyForm(): CaseFormData {
  return {
    title: "", description: "", context: "", author: "Пользователь",
    scenarioType: "", domain: "", authorCollection: "", negotiationType: "классика",
    maxRounds: 8, difficulty: "medium", rolePairs: [emptyRolePair()],
  };
}

function CaseFormDialog({
  open, onOpenChange, title, form, setForm, onSave, isPending, saveLabel
}: {
  open: boolean; onOpenChange: (v: boolean) => void; title: string;
  form: CaseFormData; setForm: (f: CaseFormData) => void;
  onSave: () => void; isPending: boolean; saveLabel: string;
}) {
  const { data: filters } = useQuery<Filters>({ queryKey: ["/api/filters"] });

  const updateRolePair = (idx: number, field: keyof RolePair, value: string) => {
    const updated = [...form.rolePairs];
    updated[idx] = { ...updated[idx], [field]: value };
    setForm({ ...form, rolePairs: updated });
  };

  const addRolePair = () => {
    setForm({ ...form, rolePairs: [...form.rolePairs, emptyRolePair()] });
  };

  const removeRolePair = (idx: number) => {
    if (form.rolePairs.length <= 1) return;
    const updated = form.rolePairs.filter((_, i) => i !== idx);
    setForm({ ...form, rolePairs: updated });
  };

  const isValid = form.title && form.description && form.context &&
    form.rolePairs.length > 0 &&
    form.rolePairs.every(rp => rp.roleA && rp.roleB && rp.goalA && rp.goalB);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          {/* Basic info */}
          <div>
            <Label className="text-xs mb-1 block">Название кейса</Label>
            <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
              placeholder="Закупка оборудования" data-testid="input-case-title" />
          </div>
          <div>
            <Label className="text-xs mb-1 block">Описание</Label>
            <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
              placeholder="Краткое описание ситуации" rows={3} data-testid="input-case-description" />
          </div>
          <div>
            <Label className="text-xs mb-1 block">Контекст (для ботов)</Label>
            <Textarea value={form.context} onChange={e => setForm({ ...form, context: e.target.value })}
              placeholder="Подробный контекст переговорной ситуации" rows={4} data-testid="input-case-context" />
          </div>

          {/* Metadata row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs mb-1 block">Автор</Label>
              <Input value={form.author} onChange={e => setForm({ ...form, author: e.target.value })}
                placeholder="Автор кейса" data-testid="input-case-author" />
            </div>
            <div>
              <Label className="text-xs mb-1 block">Тип сценария</Label>
              <Select value={form.scenarioType || "__empty__"} onValueChange={v => setForm({ ...form, scenarioType: v === "__empty__" ? "" : v })}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="Выберите тип" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__empty__">Не указан</SelectItem>
                  {(filters?.scenarioTypes || []).map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs mb-1 block">Предметная область</Label>
              <Select value={form.domain || "__empty__"} onValueChange={v => setForm({ ...form, domain: v === "__empty__" ? "" : v })}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="Выберите область" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__empty__">Не указана</SelectItem>
                  {(filters?.domainTypes || []).map(d => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs mb-1 block">Коллекция автора</Label>
              <Select value={form.authorCollection || "__empty__"} onValueChange={v => setForm({ ...form, authorCollection: v === "__empty__" ? "" : v })}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="Выберите коллекцию" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__empty__">Не указана</SelectItem>
                  {(filters?.authorCollections || []).map(a => (
                    <SelectItem key={a} value={a}>{a}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs mb-1 block">Тип переговоров</Label>
              <Select value={form.negotiationType} onValueChange={v => setForm({ ...form, negotiationType: v })}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(filters?.negotiationTypes || ["экспресс", "классика"]).map(n => (
                    <SelectItem key={n} value={n}>{n}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs mb-1 block">Сложность</Label>
              <Select value={form.difficulty} onValueChange={v => setForm({ ...form, difficulty: v })}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DIFFICULTY_OPTIONS.map(d => (
                    <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs mb-1 block">Макс. раундов</Label>
              <Input type="number" min={2} max={20} value={form.maxRounds}
                onChange={e => setForm({ ...form, maxRounds: parseInt(e.target.value) || 8 })}
                data-testid="input-case-rounds" />
            </div>
          </div>

          {/* Role pairs */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                <Users className="w-3 h-3" /> Ролевые пары
              </Label>
              <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={addRolePair} data-testid="button-add-role-pair">
                <Plus className="w-3 h-3" /> Добавить пару
              </Button>
            </div>
            {form.rolePairs.map((rp, idx) => (
              <Card key={rp.id} className="p-3 space-y-2 relative">
                {form.rolePairs.length > 1 && (
                  <Button variant="ghost" size="sm"
                    className="absolute top-1 right-1 h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                    onClick={() => removeRolePair(idx)} data-testid={`button-remove-rp-${idx}`}>
                    <X className="w-3 h-3" />
                  </Button>
                )}
                <div className="text-xs font-medium text-muted-foreground">Пара {idx + 1}</div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs mb-0.5 block">Роль A</Label>
                    <Input value={rp.roleA} onChange={e => updateRolePair(idx, "roleA", e.target.value)}
                      placeholder="Покупатель" className="h-8 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs mb-0.5 block">Роль B</Label>
                    <Input value={rp.roleB} onChange={e => updateRolePair(idx, "roleB", e.target.value)}
                      placeholder="Продавец" className="h-8 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs mb-0.5 block">Цель A</Label>
                    <Textarea value={rp.goalA} onChange={e => updateRolePair(idx, "goalA", e.target.value)}
                      placeholder="Цель участника A" rows={2} className="text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs mb-0.5 block">Цель B</Label>
                    <Textarea value={rp.goalB} onChange={e => updateRolePair(idx, "goalB", e.target.value)}
                      placeholder="Цель участника B" rows={2} className="text-sm" />
                  </div>
                </div>
              </Card>
            ))}
          </div>

          <Button className="w-full" disabled={!isValid || isPending} onClick={onSave} data-testid="button-save-case">
            {isPending ? "Сохраняю..." : saveLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function CasesPage() {
  const { isAuthenticated, login, register } = useAuth();
  const { toast } = useToast();
  const [scenarioFilter, setScenarioFilter] = useState(ALL_VALUE);
  const [domainFilter, setDomainFilter] = useState(ALL_VALUE);
  const [authorFilter, setAuthorFilter] = useState(ALL_VALUE);
  const [typeFilter, setTypeFilter] = useState(ALL_VALUE);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<CaseFormData>(emptyForm());

  const [editCase, setEditCase] = useState<NegotiationCase | null>(null);
  const [editForm, setEditForm] = useState<CaseFormData>(emptyForm());

  // Auth form state
  const [authTab, setAuthTab] = useState<"login" | "register">("login");
  const [authEmail, setAuthEmail] = useState("");
  const [authPassword, setAuthPassword] = useState("");
  const [authLoading, setAuthLoading] = useState(false);

  const { data: filters } = useQuery<Filters>({ queryKey: ["/api/filters"], enabled: isAuthenticated });
  const { data: cases, isLoading } = useQuery<NegotiationCase[]>({
    queryKey: ["/api/cases"],
    staleTime: 0,
    refetchOnMount: "always",
    enabled: isAuthenticated,
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/cases", createForm);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/cases"] });
      setCreateOpen(false);
      setCreateForm(emptyForm());
      toast({ title: "Кейс создан" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!editCase) return;
      const res = await apiRequest("PUT", `/api/cases/${editCase.id}`, editForm);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/cases"] });
      setEditCase(null);
      toast({ title: "Кейс обновлён" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/cases/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/cases"] });
      toast({ title: "Кейс удалён" });
    },
  });

  const openEditCase = (c: NegotiationCase) => {
    setEditCase(c);
    setEditForm({
      title: c.title,
      description: c.description,
      context: c.context,
      author: c.author,
      scenarioType: c.scenarioType,
      domain: c.domain,
      authorCollection: c.authorCollection,
      negotiationType: c.negotiationType,
      maxRounds: c.maxRounds,
      difficulty: c.difficulty,
      rolePairs: c.rolePairs.length > 0 ? c.rolePairs : [emptyRolePair()],
    });
  };

  const activeFilterCount = [scenarioFilter, domainFilter, authorFilter, typeFilter].filter(v => v !== ALL_VALUE).length;

  const clearFilters = () => {
    setScenarioFilter(ALL_VALUE);
    setDomainFilter(ALL_VALUE);
    setAuthorFilter(ALL_VALUE);
    setTypeFilter(ALL_VALUE);
  };

  const filtered = (cases || []).filter(c => {
    if (scenarioFilter !== ALL_VALUE && c.scenarioType !== scenarioFilter) return false;
    if (domainFilter !== ALL_VALUE && c.domain !== domainFilter) return false;
    if (authorFilter !== ALL_VALUE && c.authorCollection !== authorFilter) return false;
    if (typeFilter !== ALL_VALUE && c.negotiationType !== typeFilter) return false;
    return true;
  });

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * ITEMS_PER_PAGE, safePage * ITEMS_PER_PAGE);

  const handleAuth = async () => {
    setAuthLoading(true);
    try {
      if (authTab === "login") await login(authEmail, authPassword);
      else await register(authEmail, authPassword);
      toast({ title: authTab === "login" ? "Вход выполнен" : "Регистрация успешна" });
    } catch (err: any) {
      toast({ title: "Ошибка", description: err.message, variant: "destructive" });
    } finally { setAuthLoading(false); }
  };

  if (!isAuthenticated) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        <div>
          <h1 className="text-xl font-bold tracking-tight">База кейсов</h1>
          <p className="text-sm text-muted-foreground mt-1">Войдите для доступа к каталогу кейсов</p>
        </div>
        <Card className="max-w-md mx-auto p-6">
          <div className="text-center mb-4">
            <LogIn className="w-8 h-8 mx-auto mb-2 text-primary opacity-60" />
            <p className="text-sm text-muted-foreground">Требуется авторизация</p>
          </div>
          <div className="flex gap-2 mb-4">
            <Button variant={authTab === "login" ? "default" : "outline"} size="sm" className="flex-1" onClick={() => setAuthTab("login")}>Вход</Button>
            <Button variant={authTab === "register" ? "default" : "outline"} size="sm" className="flex-1" onClick={() => setAuthTab("register")}>Регистрация</Button>
          </div>
          <div className="space-y-3">
            <Input value={authEmail} onChange={e => setAuthEmail(e.target.value)} placeholder="Email" type="email" />
            <Input value={authPassword} onChange={e => setAuthPassword(e.target.value)} placeholder="Пароль" type="password" />
            <Button className="w-full" disabled={authLoading || !authEmail || !authPassword} onClick={handleAuth}>
              {authLoading ? "Подождите..." : authTab === "login" ? "Войти" : "Зарегистрироваться"}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight">База кейсов</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Каталог переговорных кейсов с ролевыми парами
          </p>
        </div>
        <Button size="sm" className="gap-2" onClick={() => { setCreateForm(emptyForm()); setCreateOpen(true); }} data-testid="button-create-case">
          <Plus className="w-4 h-4" /> Создать кейс
        </Button>
      </div>

      {/* Create dialog */}
      <CaseFormDialog
        open={createOpen} onOpenChange={setCreateOpen}
        title="Новый кейс"
        form={createForm} setForm={setCreateForm}
        onSave={() => createMutation.mutate()}
        isPending={createMutation.isPending}
        saveLabel="Создать кейс"
      />

      {/* Edit dialog */}
      <CaseFormDialog
        open={!!editCase} onOpenChange={(open) => { if (!open) setEditCase(null); }}
        title="Редактирование кейса"
        form={editForm} setForm={setEditForm}
        onSave={() => updateMutation.mutate()}
        isPending={updateMutation.isPending}
        saveLabel="Сохранить изменения"
      />

      {/* Filters */}
      <Card className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <Filter className="w-4 h-4" />
            Фильтры
            {activeFilterCount > 0 && (
              <Badge variant="secondary" className="text-xs px-1.5 py-0">
                {activeFilterCount}
              </Badge>
            )}
          </div>
          {activeFilterCount > 0 && (
            <Button
              variant="ghost" size="sm" onClick={clearFilters}
              className="text-xs h-7 gap-1" data-testid="clear-filters"
            >
              <X className="w-3 h-3" /> Сбросить
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="space-y-1.5">
            <label className="text-xs text-muted-foreground flex items-center gap-1.5">
              <Briefcase className="w-3 h-3" /> Сценарий
            </label>
            <Select value={scenarioFilter} onValueChange={setScenarioFilter}>
              <SelectTrigger data-testid="filter-scenario" className="h-9 text-sm">
                <SelectValue placeholder="Все сценарии" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_VALUE}>Все сценарии</SelectItem>
                {(filters?.scenarioTypes || []).map(s => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs text-muted-foreground flex items-center gap-1.5">
              <Scale className="w-3 h-3" /> Предметная область
            </label>
            <Select value={domainFilter} onValueChange={setDomainFilter}>
              <SelectTrigger data-testid="filter-domain" className="h-9 text-sm">
                <SelectValue placeholder="Все области" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_VALUE}>Все области</SelectItem>
                {(filters?.domainTypes || []).map(d => (
                  <SelectItem key={d} value={d}>{d}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs text-muted-foreground flex items-center gap-1.5">
              <Tag className="w-3 h-3" /> Автор
            </label>
            <Select value={authorFilter} onValueChange={setAuthorFilter}>
              <SelectTrigger data-testid="filter-author" className="h-9 text-sm">
                <SelectValue placeholder="Все авторы" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_VALUE}>Все авторы</SelectItem>
                {(filters?.authorCollections || []).map(a => (
                  <SelectItem key={a} value={a}>{a}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs text-muted-foreground flex items-center gap-1.5">
              <Zap className="w-3 h-3" /> Тип переговоров
            </label>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger data-testid="filter-type" className="h-9 text-sm">
                <SelectValue placeholder="Все типы" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_VALUE}>Все типы</SelectItem>
                {(filters?.negotiationTypes || []).map(n => (
                  <SelectItem key={n} value={n}>{n}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* Results count + pagination info */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {isLoading ? "Загрузка..." : `Найдено: ${filtered.length} ${filtered.length === 1 ? "кейс" : filtered.length < 5 ? "кейса" : "кейсов"}`}
        </div>
        {totalPages > 1 && (
          <div className="flex items-center gap-2 text-sm">
            <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={safePage <= 1}
              onClick={() => setPage(safePage - 1)}><ChevronLeft className="w-4 h-4" /></Button>
            <span className="text-xs text-muted-foreground">{safePage} / {totalPages}</span>
            <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={safePage >= totalPages}
              onClick={() => setPage(safePage + 1)}><ChevronRight className="w-4 h-4" /></Button>
          </div>
        )}
      </div>

      {/* Case list */}
      <div className="space-y-3">
        {paged.map(c => {
          const isExpanded = expandedId === c.id;
          return (
            <Card
              key={c.id}
              className="overflow-hidden transition-colors hover:border-primary/20"
              data-testid={`case-card-${c.id}`}
            >
              {/* Main row */}
              <button
                onClick={() => setExpandedId(isExpanded ? null : c.id)}
                className="w-full text-left p-4 flex items-start gap-4"
                data-testid={`case-toggle-${c.id}`}
              >
                <div className="flex-1 min-w-0 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-semibold text-sm leading-snug">{c.title}</h3>
                    <div className="flex items-center gap-1 shrink-0">
                      {isExpanded ? (
                        <ChevronUp className="w-4 h-4 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-muted-foreground" />
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">
                    {c.description}
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    {c.scenarioType && (
                      <Badge variant="outline" className="text-xs gap-1">
                        <Briefcase className="w-3 h-3" />
                        {c.scenarioType}
                      </Badge>
                    )}
                    {c.domain && (
                      <Badge variant="outline" className="text-xs gap-1">
                        <Scale className="w-3 h-3" />
                        {c.domain}
                      </Badge>
                    )}
                    {c.authorCollection && (
                      <Badge variant="secondary" className="text-xs gap-1">
                        <Tag className="w-3 h-3" />
                        {c.authorCollection}
                      </Badge>
                    )}
                    {c.negotiationType && (
                      <Badge
                        variant="secondary"
                        className={`text-xs gap-1 ${c.negotiationType === "экспресс" ? "bg-primary/10 text-primary border-primary/20" : ""}`}
                      >
                        <Zap className="w-3 h-3" />
                        {c.negotiationType}
                      </Badge>
                    )}
                    <Badge variant="outline" className="text-xs gap-1">
                      <Users className="w-3 h-3" />
                      {c.rolePairs.length} {c.rolePairs.length === 1 ? "пара" : c.rolePairs.length < 5 ? "пары" : "пар"}
                    </Badge>
                  </div>
                </div>
              </button>

              {/* Expanded: role pairs + edit/delete */}
              {isExpanded && (
                <div className="border-t border-border/50 bg-muted/20 p-4 space-y-3">
                  <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                    <BookOpen className="w-3 h-3" />
                    Контекст
                  </div>
                  <p className="text-sm leading-relaxed">{c.context}</p>

                  <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground flex items-center gap-1.5 mt-4">
                    <Users className="w-3 h-3" />
                    Ролевые пары
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {c.rolePairs.map((rp: RolePair) => (
                      <Card key={rp.id} className="p-3 space-y-2 bg-background">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">{rp.roleA}</span>
                          <span className="text-xs text-muted-foreground">vs</span>
                          <span className="text-sm font-medium">{rp.roleB}</span>
                        </div>
                        <div className="space-y-1">
                          <p className="text-xs text-muted-foreground">
                            <span className="font-medium text-foreground/80">{rp.roleA}:</span> {rp.goalA}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            <span className="font-medium text-foreground/80">{rp.roleB}:</span> {rp.goalB}
                          </p>
                        </div>
                      </Card>
                    ))}
                  </div>

                  <div className="flex items-center justify-between pt-2">
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span>{c.maxRounds} раундов</span>
                      <span>•</span>
                      <span>{c.difficulty === "hard" ? "Сложный" : c.difficulty === "easy" ? "Простой" : "Средний"}</span>
                      <span>•</span>
                      <span>{c.author}</span>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" className="h-7 text-xs gap-1"
                        onClick={(e) => { e.stopPropagation(); openEditCase(c); }}
                        data-testid={`button-edit-case-${c.id}`}>
                        <Pencil className="w-3 h-3" /> Редактировать
                      </Button>
                      <Button variant="outline" size="sm" className="h-7 text-xs gap-1 text-destructive hover:text-destructive"
                        onClick={(e) => { e.stopPropagation(); deleteMutation.mutate(c.id); }}
                        data-testid={`button-delete-case-${c.id}`}>
                        <Trash2 className="w-3 h-3" /> Удалить
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </Card>
          );
        })}

        {!isLoading && filtered.length === 0 && (
          <Card className="p-8 text-center text-muted-foreground">
            <BookOpen className="w-8 h-8 mx-auto mb-3 opacity-50" />
            <p className="text-sm">Кейсов не найдено</p>
            <p className="text-xs mt-1">Попробуйте изменить фильтры или создайте новый кейс</p>
          </Card>
        )}
      </div>

      {/* Bottom pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <Button variant="outline" size="sm" className="h-8 gap-1" disabled={safePage <= 1}
            onClick={() => setPage(safePage - 1)}><ChevronLeft className="w-4 h-4" /> Назад</Button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
            <Button key={p} variant={p === safePage ? "default" : "outline"} size="sm" className="h-8 w-8 p-0"
              onClick={() => setPage(p)}>{p}</Button>
          ))}
          <Button variant="outline" size="sm" className="h-8 gap-1" disabled={safePage >= totalPages}
            onClick={() => setPage(safePage + 1)}>Далее <ChevronRight className="w-4 h-4" /></Button>
        </div>
      )}
    </div>
  );
}
